// @flow

/**
 * This file provides functions to register async bundle (reducers/saga)
 * load time from bundle's index config
 */

import { emptyObject } from 'Lib/constants/placeholder';

import {
  asyncBundleIndex,
  asyncInitBundleIndex,
  type BundleConfigType,
} from 'Store/dynamicLoading';
import dynamicReducer from 'Store/dynamicLoading/ReducerRegistry';
import type { InitOption } from 'Store/dynamicLoading/utils';

type BundleShape = {
  id: string,
  options: InitOption,
};

const loadReducerAndRegister = async (id: string, options: InitOption) => {
  const { loadReducer } = options;

  const { reducer, persistConfig } = await loadReducer();
  dynamicReducer.register(id, reducer, persistConfig);
};

const registerReducer = async (bundle: BundleShape) => {
  const { id, options } = bundle;
  await loadReducerAndRegister(id, options);
};

/**
 * Load index and reducer
 */
export const loadBundleIndexAndRegister = async (key: string) => {
  const bundle = await import(`Store/${key}/index` /* webpackChunkName: "async-[request]" */);

  registerReducer(bundle.default);
};

/**
 * Entry function to register all async bundle based on timing
 */
export const registerAsyncBundles = () => {
  asyncInitBundleIndex.forEach(({ name }: BundleConfigType) => {
    loadBundleIndexAndRegister(name);
  });
};

/**
 * Create initial state shape for root reducer to prevent selector breaking
 */
export const createAsyncReducerState = (baseState: { [key: string]: any } = {}) => {
  const reducers = {};
  asyncBundleIndex.forEach(({ name: key, initialState = emptyObject }: BundleConfigType) => {
    const stateToMerge = baseState[key] || {};
    if (!reducers[key]) {
      // default reducer is pure js shape
      let reducer = initialState;
      try {
        // eslint-disable-next-line import/no-dynamic-require
        reducer = require(`Store/${key}/initialState`).default;
      } catch (e) {} // eslint-disable-line no-empty

      reducers[key] = () => ({
        ...reducer,
        ...stateToMerge,
      });
    }
  });
  return reducers;
};
